/**
 * Wave 5 — Clarifications panel with impact badges.
 */
import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  List,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatDate } from '@/utils/format';
import {
  buildClarificationImpact,
  impactLevelColor,
} from './clarificationAddendaNegotiationUx';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

export type EpcClarificationsPanelProps = {
  tenderId: string;
  rows: Record<string, unknown>[];
  onUpdated?: () => void;
};

export const EpcClarificationsPanel: React.FC<EpcClarificationsPanelProps> = ({
  tenderId,
  rows,
  onUpdated,
}) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const createM = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcCommercialService.createClarification(values),
    onSuccess: () => {
      message.success('Clarification created');
      setOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const patchM = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      epcCommercialService.patchClarification(id, payload),
    onSuccess: () => {
      message.success('Clarification updated');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Update failed'),
  });

  const highCount = rows.filter((r) => buildClarificationImpact(r).level === 'HIGH').length;

  return (
    <Card
      size="small"
      title={
        <Space wrap>
          <span>Clarifications</span>
          {highCount > 0 ? <Tag color="error">{highCount} high impact</Tag> : null}
        </Space>
      }
      extra={
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setOpen((v) => !v)}>
          New
        </Button>
      }
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Impact badges from due date, open/answered status, and declared impact areas in meta — not
        invented scores.
      </Paragraph>

      {!rows.length ? (
        <Alert type="info" showIcon message="No clarifications yet" />
      ) : (
        <List
          size="small"
          dataSource={rows}
          renderItem={(row) => {
            const impact = buildClarificationImpact(row);
            const id = String(row.id);
            return (
              <List.Item
                actions={[
                  <Select
                    key="status"
                    size="small"
                    style={{ width: 120 }}
                    value={String(row.status || 'Open')}
                    options={[
                      { value: 'Open', label: 'Open' },
                      { value: 'Answered', label: 'Answered' },
                      { value: 'Closed', label: 'Closed' },
                    ]}
                    onChange={(status) => patchM.mutate({ id, payload: { status } })}
                  />,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space wrap>
                      <Text strong>{String(row.clarification_number || id.slice(0, 8))}</Text>
                      <Tag color={impactLevelColor(impact.level)}>{impact.level}</Tag>
                      {impact.badges.map((b) => (
                        <Tag key={b.key} color={b.color}>
                          {b.label}
                        </Tag>
                      ))}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <Text>{String(row.question || row.subject || '—')}</Text>
                      <Text type="secondary">
                        Due: {row.due_at ? formatDate(String(row.due_at)) : '—'}
                        {row.response ? ` · Response on file` : ''}
                      </Text>
                      {impact.reasons.length ? (
                        <Text type="secondary">{impact.reasons.join(' · ')}</Text>
                      ) : null}
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}

      {open ? (
        <Card size="small" title="New clarification" style={{ marginTop: 12 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            createM.mutate({
              tenderId,
              question: values.question,
              dueAt: values.dueAt ? dayjs(values.dueAt).format('YYYY-MM-DD') : undefined,
              metaJson: values.impactAreas?.length
                ? { impactAreas: values.impactAreas }
                : undefined,
            });
          }}
        >
          <Form.Item
            name="question"
            label="Question"
            rules={[{ required: true, message: 'Required' }]}
          >
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="dueAt" label="Due date">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="impactAreas" label="Impact areas (optional)">
            <Select
              mode="multiple"
              allowClear
              options={[
                { value: 'scope', label: 'Scope' },
                { value: 'price', label: 'Price' },
                { value: 'schedule', label: 'Schedule' },
                { value: 'commercial', label: 'Commercial' },
                { value: 'technical', label: 'Technical' },
              ]}
            />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={createM.isPending}>
              Save
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Space>
        </Form>
        </Card>
      ) : null}
    </Card>
  );
};

export default EpcClarificationsPanel;
